import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle } from "lucide-react";
import { CARD, NUM, formatWhen } from "../lib/dashboardTokens";
import { useEscalationRateDetail } from "../lib/api";
import { ladderLabel } from "../lib/workshopAnalytics";

// The escalated groups behind one row (or one ladder cell) of Workshop
// Analytics' "Service cases escalated" table — reached by clicking that
// row/cell. See docs/workshop-analytics-plan.md §2.4; the aggregate endpoint
// it reads from has no group-level detail, so this page has its own.
export default function EscalationDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const by = searchParams.get("by") || "workshop";
  const key = searchParams.get("key") || "";
  const label = searchParams.get("label") || key;
  const ladder = searchParams.get("ladder") || undefined;
  const locations = searchParams.get("locations") || undefined;

  const { data, isLoading, isError, error } = useEscalationRateDetail({ from, to, by, key, ladder, locations });
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="escalation-detail-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-900"
        data-testid="escalation-detail-back"
      >
        <ArrowLeft size={15} /> Back to Workshop Analytics
      </button>

      <div>
        <h1 className="font-work text-2xl font-semibold tracking-tight text-slate-900">{label}</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          {ladder ? `${ladderLabel(ladder)} · ` : ""}
          {from} – {to}
        </p>
      </div>

      <div className={CARD}>
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-slate-400">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-600">{error?.response?.data?.detail || "Could not load these escalations."}</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="escalation-detail-empty">
            No escalations in this period.
          </p>
        ) : (
          <div className="overflow-x-auto" data-testid="escalation-detail-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="py-2 pr-4 text-xs font-medium text-slate-500">Group</th>
                  <th className="py-2 pr-4 text-xs font-medium text-slate-500">Customer</th>
                  {by === "advisor" ? (
                    <th className="py-2 pr-4 text-xs font-medium text-slate-500">Workshop</th>
                  ) : (
                    <th className="py-2 pr-4 text-xs font-medium text-slate-500">Advisor</th>
                  )}
                  <th className="py-2 pr-4 text-xs font-medium text-slate-500">Type</th>
                  <th className="py-2 pr-0 text-right text-xs font-medium text-slate-500">Raised</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.group_row_id}-${r.ladder}-${i}`} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 pr-4">
                      <Link
                        to={`/chat-history?group=${encodeURIComponent(r.group_name)}`}
                        className="inline-flex items-center gap-1.5 font-medium text-blue-600 hover:underline"
                        data-testid={`escalation-detail-group-${r.group_row_id}`}
                      >
                        <MessageCircle size={13} /> {r.group_name || "Untitled group"}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 text-slate-700">{r.customer_name || "—"}</td>
                    <td className="py-2 pr-4 text-slate-700">
                      {by === "advisor" ? r.workshop_location : r.service_advisor_name || "—"}
                    </td>
                    <td className="py-2 pr-4 text-slate-700">{ladderLabel(r.ladder)}</td>
                    <td className={`py-2 pr-0 text-right text-slate-600 whitespace-nowrap ${NUM}`}>{formatWhen(r.occurred_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
